import { useTranslation } from "react-i18next";
import { Box, Skeleton, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";

import { colors } from "../../../theme/palette";
import { GenderAgeGroupData } from "../../../types";

const chartSetting = {
  width: 391,
  height: 310,
};

const Audience = ({ dataset, isLoading }: { dataset?: GenderAgeGroupData; isLoading: boolean }) => {
  const { t } = useTranslation();

  const totalWomen = dataset ? Object.values(dataset.FEMALE || {}).reduce((sum, count) => sum + count, 0) : 0;
  const totalMale = dataset ? Object.values(dataset.MALE || {}).reduce((sum, count) => sum + count, 0) : 0;
  const total = totalWomen + totalMale;

  const womenPercentage = total > 0 ? ((totalWomen / total) * 100).toFixed(1) : null;
  const malePercentage = total > 0 ? ((totalMale / total) * 100).toFixed(1) : null;

  const barChartData = dataset
    ? Object.keys(dataset.FEMALE || {}).map((ageRange) => ({
        ageRange,
        women: dataset.FEMALE[ageRange] || 0,
        male: dataset.MALE[ageRange] || 0,
      }))
    : [];

  return (
    <Box
      sx={{
        width: "391px",
        height: "fit-content",
      }}
    >
      <Typography
        sx={{
          color: colors.Black100,
          fontFamily: "Inter",
          fontSize: "14px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "normal",
          marginBottom: "36px",
        }}
      >
        {t("pages.dashboard.audience")}
      </Typography>
      {isLoading ? (
        <LoadingBarChart />
      ) : total > 0 ? (
        <BarChart
          margin={{
            bottom: 60,
          }}
          grid={{ horizontal: true }}
          dataset={barChartData}
          borderRadius={20}
          xAxis={[
            {
              scaleType: "band",
              dataKey: "ageRange",
              // @ts-ignore
              categoryGapRatio: 0.5,
              barGapRatio: 0.5,
              tickPlacement: "middle",
            },
          ]}
          series={[
            {
              dataKey: "women",
              label: total > 0 ? `${t("constant.gender.female")} ${womenPercentage}%` : undefined,
              color: "#07E098",
            },
            {
              dataKey: "male",
              label: total > 0 ? `${t("constant.gender.male")} ${malePercentage}%` : undefined,
              color: "#FFAB00",
            },
          ]}
          {...chartSetting}
          slotProps={{
            legend: {
              position: { vertical: "bottom", horizontal: "middle" },
              direction: "row",
              padding: -10,
              itemMarkWidth: 7,
              itemMarkHeight: 6,
              markGap: 6,
              itemGap: 33,
              labelStyle: {
                fontSize: "12px",
                fill: "#919EAB",
              },
            },
          }}
          sx={{
            "& .MuiChartsGrid-line": {
              strokeDasharray: "4 4",
              stroke: "#EAECF0",
            },
          }}
        />
      ) : (
        <Typography variant="body16Regular">{t("pages.dashboard.noFollowersToDisplay")}</Typography>
      )}
    </Box>
  );
};

export default Audience;

const LoadingBarChart = () => {
  const barHeights = [250, 150, 280, 180, 90, 130, 220];

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        rowGap: "20px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "290px",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-around",
          borderBottom: "2px solid #f0f0f0",
          borderLeft: "2px solid #f0f0f0",
        }}
      >
        {barHeights.map((height, index) => (
          <Box
            sx={{
              display: "flex",
              columnGap: "5px",
              alignItems: "flex-end",
            }}
          >
            <Skeleton
              key={index}
              variant="rectangular"
              width="10px"
              height={`${height / 1.5}px`}
              sx={{
                backgroundColor: "#e0e0e0",
              }}
            />
            <Skeleton
              key={index}
              variant="rectangular"
              width="10px"
              height={`${height}px`}
              sx={{
                backgroundColor: "#e0e0e0",
              }}
            />
          </Box>
        ))}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", columnGap: "40px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            columnGap: "8px",
            alignItems: "center",
          }}
        >
          <Skeleton variant="rectangular" width={7} height={7} />
          <Skeleton variant="rounded" width={80} height={16} />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            columnGap: "8px",
            alignItems: "center",
          }}
        >
          <Skeleton variant="rectangular" width={7} height={7} />
          <Skeleton variant="rounded" width={60} height={16} />
        </Box>
      </Box>
    </Box>
  );
};
